import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../../context/AppContext';

/**
 * Route guard that verifies the session cookie before rendering children.
 * While the check is in-flight a minimal loading state is shown, preventing
 * any flash of authenticated content for unauthenticated users.
 *
 * If the token is valid but `dietist` is missing from context (e.g. the tab
 * was opened without localStorage data), the guard fetches the dietist profile
 * and populates the context before rendering children. This prevents pages
 * that depend on `dietist` from mounting with a null value.
 *
 * Usage in App.js:
 *   <ProtectedRoute>
 *       <HomePage />
 *   </ProtectedRoute>
 */
const ProtectedRoute = ({ children }) => {
    const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'
    const { dietist, setDietist } = useAppContext();

    useEffect(() => {
        let cancelled = false;

        const verifyToken = async () => {
            try {
                const { data } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/auth/verify-token`,
                    { params: { verifyOnly: true }, withCredentials: true }
                );
                // If dietist is not in context (e.g. localStorage was cleared or
                // the tab was opened via auto-redirect without a setDietist call),
                // fetch the profile using the userId returned by the token check.
                if (!dietist && data.userId) {
                    const profileRes = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/dietists/${data.userId}`,
                        { withCredentials: true }
                    );
                    if (!cancelled) setDietist(profileRes.data);
                }
                if (!cancelled) setAuthState('authenticated');
            } catch {
                if (!cancelled) setAuthState('unauthenticated');
            }
        };

        verifyToken();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (authState === 'loading') {
        return null; // render nothing until auth is confirmed
    }

    if (authState === 'unauthenticated') {
        return <Navigate to="/login" replace />;
    }

    return children;
};

export default ProtectedRoute;
